import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ExcelImporter } from '../components/import/ExcelImporter';
import { DatabaseBackup } from '../components/backup/DatabaseBackup';
import { 
  importProperties, 
  getPropertyTemplateFields, 
  exportPropertiesToExcel
} from '../services/importData';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

interface Property {
  id: string;
  name: string;
}

export const DataManagementPage = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');

  // Cargar propiedades al montar el componente
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const { data, error } = await supabase
          .from('properties')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setProperties(data || []);
        if (data && data.length > 0) {
          setSelectedPropertyId(data[0].id);
        }
      } catch (error: any) {
        console.error('Error al cargar propiedades:', error);
      }
    };

    fetchProperties();
  }, []);

  const handleCopyPropertyId = async () => {
    if (!selectedPropertyId) {
      toast.error('Selecciona una propiedad primero');
      return;
    }

    try {
      await navigator.clipboard.writeText(selectedPropertyId);
      toast.success('ID copiado al portapapeles');
    } catch (error) {
      toast.error('Error al copiar el ID');
    }
  };

  const handlePropertiesImport = async (data: any[]) => {
    try {
      await importProperties(data);
      return Promise.resolve();
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const handleExportProperties = async () => {
    setExportLoading(true);
    try {
      const properties = await exportPropertiesToExcel();
      
      // Crear un libro de trabajo
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(properties);
      XLSX.utils.book_append_sheet(wb, ws, 'Propiedades');
      
      // Descargar el archivo
      XLSX.writeFile(wb, `propiedades_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.success('Propiedades exportadas correctamente');
    } catch (error: any) {
      console.error('Error al exportar propiedades:', error);
      toast.error(error.message || 'Error al exportar propiedades');
    } finally {
      setExportLoading(false);
    }
  };



  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Gestión de Datos</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Importación de propiedades */}
          <Card>
            <div className="p-4">
              <h2 className="text-xl font-semibold mb-4">Importar Datos</h2>
              <ExcelImporter
                onDataImported={handlePropertiesImport}
                templateFields={getPropertyTemplateFields()}
                entityName="propiedades"
              />
            </div>
          </Card>

          {/* Exportación de propiedades */}
          <Card>
            <div className="p-4">
              <h2 className="text-xl font-semibold mb-4">Exportar Propiedades</h2>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-500 mb-3">
                  Descarga todas las propiedades en formato Excel para su análisis o modificación.
                </p>
                <Button 
                  onClick={handleExportProperties} 
                  loading={exportLoading}
                >
                  Exportar Propiedades
                </Button>
              </div>
            </div>
          </Card>
          
          {/* Consultar IDs de Propiedades */}
          <Card>
            <div className="p-4">
              <h2 className="text-xl font-semibold mb-4">Consultar IDs de Propiedades</h2>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-500 mb-3">
                  Obtén el ID de cualquier propiedad para usar en importaciones masivas.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selecciona una propiedad:
                  </label>
                  <select
                    value={selectedPropertyId}
                    onChange={(e) => setSelectedPropertyId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecciona una propiedad...</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedPropertyId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ID de la propiedad:
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={selectedPropertyId}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                      />
                      <Button
                        onClick={handleCopyPropertyId}
                        className="px-4 py-2 text-sm"
                      >
                        📋 Copiar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Copia de seguridad avanzada */}
          <DatabaseBackup />
        </div>

        {/* Instrucciones */}
        <Card>
          <div className="p-4">
            <h2 className="text-xl font-semibold mb-4">Instrucciones</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Importación de Datos</h3>
                <ol className="list-decimal list-inside mt-2 space-y-1 text-sm text-gray-600">
                  <li>Descarga la plantilla de Excel haciendo clic en "Descargar Plantilla"</li>
                  <li>Completa los datos en la plantilla siguiendo el formato indicado</li>
                  <li>Guarda el archivo y súbelo utilizando el selector de archivos</li>
                  <li>Haz clic en "Importar" para cargar los datos</li>
                  <li>Verifica que los datos se hayan importado correctamente</li>
                </ol>
              </div>
              
              <div>
                <h3 className="text-lg font-medium">Copias de Seguridad</h3>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm text-gray-600">
                  <li>Se recomienda crear copias de seguridad periódicamente</li>
                  <li>Guarda los archivos de copia de seguridad en un lugar seguro</li>
                  <li>Crea una copia antes de realizar cambios importantes en la base de datos</li>
                  <li>Las copias de seguridad incluyen todas las tablas importantes del sistema</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};
